/**
 * Admin — Homepage Layout Manager.
 * Lets the store owner reorder + toggle homepage sections without code.
 * Persisted at settings.commerce.homepage.sections via
 * PATCH /store/settings/commerce (existing free-form endpoint).
 *
 * Everything the customer Home renders comes from `useStore().homepageSections`
 * so this admin page fully controls the storefront layout.
 */
import { useEffect, useMemo, useState } from "react";
import { api, errMsg } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import SuperAdminStoreGate from "@/components/SuperAdminStoreGate";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Save, GripVertical, ArrowUp, ArrowDown, RotateCcw, LayoutTemplate, ExternalLink } from "lucide-react";
import { DEFAULT_HOMEPAGE_SECTIONS } from "@/contexts/StoreContext";

export default function HomepageLayout() {
  return (
    <SuperAdminStoreGate
      pageTitle="Homepage Layout"
      pageSubtitle="Reorder and toggle sections shown to customers on the home page."
    >
      <HomepageLayoutInner />
    </SuperAdminStoreGate>
  );
}

function HomepageLayoutInner() {
  const [sections, setSections] = useState(DEFAULT_HOMEPAGE_SECTIONS);
  const [commerceAll, setCommerceAll] = useState({}); // preserve other commerce sub-keys on save
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/store/settings");
      const commerce = data?.settings?.commerce || {};
      setCommerceAll(commerce);
      const saved = commerce.homepage?.sections;
      if (Array.isArray(saved) && saved.length) {
        // Merge stored with any new defaults so newer sections show up
        const byKey = Object.fromEntries(saved.map((s) => [s.key, s]));
        const merged = [];
        saved.forEach((s) => { if (s?.key && byKey[s.key]) merged.push({ ...DEFAULT_HOMEPAGE_SECTIONS.find((d) => d.key === s.key), ...s }); });
        DEFAULT_HOMEPAGE_SECTIONS.forEach((d) => { if (!byKey[d.key]) merged.push(d); });
        setSections(merged);
      }
    } catch (e) { toast.error(errMsg(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const move = (idx, delta) => {
    setSections((prev) => {
      const next = [...prev];
      const target = idx + delta;
      if (target < 0 || target >= next.length) return prev;
      [next[idx], next[target]] = [next[target], next[idx]];
      return next;
    });
  };
  const toggle = (idx) => {
    setSections((prev) => prev.map((s, i) => (i === idx ? { ...s, enabled: !s.enabled } : s)));
  };
  const reset = () => setSections(DEFAULT_HOMEPAGE_SECTIONS);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/store/settings/commerce", {
        ...commerceAll,
        homepage: { ...(commerceAll.homepage || {}), sections },
      });
      toast.success("Homepage layout saved");
      load();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setSaving(false); }
  };

  const enabledCount = useMemo(() => sections.filter((s) => s.enabled).length, [sections]);

  // Native HTML5 drag-and-drop reorder — no extra dependency needed.
  const [dragIdx, setDragIdx] = useState(null);
  const [dragOverIdx, setDragOverIdx] = useState(null);
  const onDragStart = (i) => (e) => { setDragIdx(i); try { e.dataTransfer.effectAllowed = "move"; } catch { /* noop */ } };
  const onDragOver  = (i) => (e) => { e.preventDefault(); if (dragOverIdx !== i) setDragOverIdx(i); };
  const onDragLeave = () => setDragOverIdx(null);
  const onDrop      = (i) => (e) => {
    e.preventDefault();
    if (dragIdx === null || dragIdx === i) { setDragIdx(null); setDragOverIdx(null); return; }
    setSections((prev) => {
      const next = [...prev];
      const [moved] = next.splice(dragIdx, 1);
      next.splice(i, 0, moved);
      return next;
    });
    setDragIdx(null); setDragOverIdx(null);
  };
  const onDragEnd = () => { setDragIdx(null); setDragOverIdx(null); };

  return (
    <div className="p-8 space-y-6" data-testid="homepage-layout-page">
      <PageHeader
        title="Homepage Layout"
        subtitle="Reorder and toggle sections shown to customers on the home page. Changes apply instantly."
        actions={
          <div className="flex gap-2">
            <Button variant="outline" asChild data-testid="hl-preview">
              <a href="/" target="_blank" rel="noreferrer"><ExternalLink className="w-4 h-4 mr-1.5" />Preview homepage</a>
            </Button>
            <Button variant="outline" onClick={reset} disabled={saving} data-testid="hl-reset"><RotateCcw className="w-4 h-4 mr-1.5" />Reset</Button>
            <Button onClick={save} disabled={saving || loading} className="btn-primary-glow" data-testid="hl-save"><Save className="w-4 h-4 mr-1.5" />{saving ? "Saving…" : "Save layout"}</Button>
          </div>
        }
      />

      <Card>
        <CardContent className="p-0">
          <div className="px-4 md:px-6 py-3 border-b bg-secondary/40 text-sm flex items-center gap-2">
            <LayoutTemplate className="w-4 h-4 text-primary" />
            <span className="font-medium">{enabledCount} of {sections.length} sections visible</span>
            <span className="text-muted-foreground text-xs ml-2 hidden md:inline">Drag rows to reorder. Sections at the top render first on the homepage.</span>
          </div>
          {loading ? (
            <div className="p-6 space-y-2">
              {Array.from({ length: 8 }).map((_, i) => <div key={i} className="h-14 rounded-lg bg-secondary/50 animate-pulse" />)}
            </div>
          ) : (
            <div className="divide-y" data-testid="hl-list">
              {sections.map((s, i) => (
                <div
                  key={s.key}
                  draggable
                  onDragStart={onDragStart(i)}
                  onDragOver={onDragOver(i)}
                  onDragLeave={onDragLeave}
                  onDrop={onDrop(i)}
                  onDragEnd={onDragEnd}
                  className={`flex items-center gap-3 px-4 md:px-6 py-3 hover:bg-secondary/30 transition ${dragIdx === i ? "opacity-40" : ""} ${dragOverIdx === i && dragIdx !== i ? "bg-primary/10 border-l-4 border-primary" : ""}`}
                  data-testid={`hl-row-${s.key}`}
                >
                  <GripVertical className="w-4 h-4 text-muted-foreground shrink-0 cursor-grab active:cursor-grabbing" />
                  <div className="w-8 text-xs font-mono text-muted-foreground">{String(i + 1).padStart(2, "0")}</div>
                  <div className="flex-1 min-w-0">
                    <div className={`font-medium ${s.enabled ? "" : "text-muted-foreground line-through"}`}>{s.label}</div>
                    <div className="text-[11px] text-muted-foreground uppercase tracking-wider">{s.key}</div>
                  </div>
                  <Switch
                    checked={!!s.enabled}
                    onCheckedChange={() => toggle(i)}
                    data-testid={`hl-toggle-${s.key}`}
                  />
                  <div className="flex flex-col">
                    <Button size="icon" variant="ghost" onClick={() => move(i, -1)} disabled={i === 0} data-testid={`hl-up-${s.key}`}><ArrowUp className="w-4 h-4" /></Button>
                    <Button size="icon" variant="ghost" onClick={() => move(i, 1)} disabled={i === sections.length - 1} data-testid={`hl-down-${s.key}`}><ArrowDown className="w-4 h-4" /></Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
